// OAUTH_PENDING_RETURN_KEY uses sessionStorage:
//   - Tab-scoped: each tab has its own pending return path (no cross-tab bleed)
//   - Survives OAuth redirect within the same tab (browser keeps sessionStorage
//     across same-tab navigations to external domains and back)
//   - Auto-cleared when the tab closes — no stale entries
//
// OAUTH_CALLBACK_STATUS_KEY stays in sessionStorage because HubSpot OAuth status
// may need to be read by a tab that re-opens after the OAuth window closes.
const OAUTH_PENDING_RETURN_KEY = "skyadvisor_oauth_pending_return";
const OAUTH_CALLBACK_STATUS_KEY = "skyadvisor_oauth_callback_status";

interface OAuthPendingReturn {
  path: string;
  timestamp: number;
}

interface OAuthCallbackStatus {
  status: "success" | "error";
  message?: string;
  timestamp: number;
}

export function storeOAuthReturnPath(path: string): void {
  const data: OAuthPendingReturn = {
    path,
    timestamp: Date.now(),
  };
  sessionStorage.setItem(OAUTH_PENDING_RETURN_KEY, JSON.stringify(data));
}

/**
 * Keep the page this tab is on, so the sign-in that is about to start returns
 * to it (Callback reads it back). The site root and /callback itself are never
 * kept: they carry no destination, and keeping them would overwrite one.
 */
export function rememberReturnPath(): void {
  const currentPath = window.location.pathname + window.location.search;
  if (currentPath !== "/" && !currentPath.startsWith("/callback")) {
    storeOAuthReturnPath(currentPath);
  }
}

export function captureOAuthCallbackStatus(): void {
  const urlParams = new URLSearchParams(window.location.search);
  const oauthStatus = urlParams.get("oauth");

  if (oauthStatus === "success" || oauthStatus === "error") {
    const data: OAuthCallbackStatus = {
      status: oauthStatus,
      message: urlParams.get("message") || undefined,
      timestamp: Date.now(),
    };
    sessionStorage.setItem(OAUTH_CALLBACK_STATUS_KEY, JSON.stringify(data));
  }
}

export function getAndClearOAuthReturnPath(): {
  path: string;
  oauthStatus?: OAuthCallbackStatus;
} | null {
  try {
    const stored = sessionStorage.getItem(OAUTH_PENDING_RETURN_KEY);
    if (!stored) {
      sessionStorage.removeItem(OAUTH_CALLBACK_STATUS_KEY);
      return null;
    }

    const data: OAuthPendingReturn = JSON.parse(stored);
    sessionStorage.removeItem(OAUTH_PENDING_RETURN_KEY);

    const maxAge = 10 * 60 * 1000;
    if (Date.now() - data.timestamp > maxAge) {
      sessionStorage.removeItem(OAUTH_CALLBACK_STATUS_KEY);
      return null;
    }

    let oauthStatus: OAuthCallbackStatus | undefined;
    const statusStored = sessionStorage.getItem(OAUTH_CALLBACK_STATUS_KEY);
    if (statusStored) {
      oauthStatus = JSON.parse(statusStored);
      sessionStorage.removeItem(OAUTH_CALLBACK_STATUS_KEY);
    }

    return { path: data.path, oauthStatus };
  } catch {
    sessionStorage.removeItem(OAUTH_PENDING_RETURN_KEY);
    sessionStorage.removeItem(OAUTH_CALLBACK_STATUS_KEY);
    return null;
  }
}
